import { MenuTitles, Pages } from 'constants/menu'
import { AgencyContext } from 'contexts'
import { ContentLayout, PageLayout } from 'layout'
import { AgxToast, enumToOptions } from '@urbanx/agx-ui-components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { StaffAction } from 'types/staff'
import { AgxToastState, NotificationState } from 'types/commonTypes'
import { UploadPublicDocument } from 'services'
import { isEqual } from 'lodash-es'

import { FormPrompt } from '../../components/FormPrompt'
import RoleAssignmentModal from './RoleAssignmentModel'
import StaffMemberFormPage, {
  StaffMemberFormValues,
} from './StaffMemberFormPage'
import { useDoesUserHasRequiredRoles } from 'helpers/doesUserHaveRequiredRoles'
import { AuthContext, StaffRole } from '@urbanx/agx.auth.client'
import {
  UploadedDocument,
  useCreateStaffMember,
  useInvalidateUseGetAllStaff,
} from '@urbanx/agx.staff.client'
import { getEnumValueByValue } from 'helpers/enumHelper'
import { isDirtyEqual } from 'utils/compares'
import {
  AgenciesApiContext,
  UrbanXTeamByAgenciesResponse,
  useAssignStaffMemberToAgencies,
  useGetAllAgencies,
  useInvalidateUseGetUrbanXTeamForAgency,
  useInvalidateUseGetStaffMembersAssignedToAgencies,
  useRemovePublicDocument,
} from '@urbanx/agx.agencies.client'
import {
  AgxButton,
  AgxEmptyState,
  AgxSpacer,
  AgxSaveChangesButton,
  Icon_Arrow,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2'
import {
  getAvailableStaffRoleOptions,
  mapAgenciesToOptions,
  rolesAssignableToAgency,
  rolesWithStateAccess,
  staffStateOptions,
  validateStaffMemberForm,
  dedupeRoleConflicts,
} from './staffPageUtils'
import { isEmailValid } from 'helpers/emailHelper'

const emptyStaffMember: StaffMemberFormValues = {}
const staffRoleOptions = enumToOptions(StaffRole)

const CreateStaffMember = () => {
  const navigate = useNavigate()
  const [selectedAgency] = useContext(AgencyContext)
  const { userAccount, getAuthToken } = useContext(AuthContext)
  const onBack = () => navigate(Pages.STAFF)

  const invalidateGetAllStaff = useInvalidateUseGetAllStaff()
  const invalidateUrbanXTeamForAgency = useInvalidateUseGetUrbanXTeamForAgency()
  const invalidateGetStaffMembersAssignedToAgencies =
    useInvalidateUseGetStaffMembersAssignedToAgencies()
  const queryClient = useQueryClient()

  const [staffMemberForm, setStaffMemberForm] = useState<StaffMemberFormValues>(
    {
      ...emptyStaffMember,
      roles: emptyStaffMember.roles,
    }
  )
  const [img, setImg] = useState<string | undefined>(undefined)
  const [saveChangesActivated, setSaveChangesActivated] = useState(false)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [forceCheck, setForceCheck] = useState(false)
  const [errorsList, setErrorsList] = useState<string[]>([])
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [pendingRoleAssignmentCheck, setPendingRoleAssignmentCheck] =
    useState(false)
  const [roleConflicts, setRoleConflicts] = useState<
    UrbanXTeamByAgenciesResponse[]
  >([])
  const [saveButtonState, setSaveButtonState] = useState<SAVE_BUTTON_STATES>(
    SAVE_BUTTON_STATES.UNSAVED
  )
  const [agencyIds, setAgencyIds] = useState<string[]>([])
  const [toastState, setToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  })

  const doesUserHaveRequiredRoles = useDoesUserHasRequiredRoles()

  const availableStaffRoleOptions = useMemo(() => {
    return getAvailableStaffRoleOptions(
      staffRoleOptions,
      doesUserHaveRequiredRoles
    )
  }, [doesUserHaveRequiredRoles])

  const selectedRoles = useMemo(
    () => staffMemberForm.roles ?? [],
    [staffMemberForm.roles]
  )

  const roleIsAssignableToAgency = useMemo(() => {
    return selectedRoles.some((role) => rolesAssignableToAgency.includes(role))
  }, [selectedRoles])

  const showStateSelect = useMemo(() => {
    return selectedRoles.some((role) =>
      rolesWithStateAccess.includes(role)
    )
  }, [selectedRoles])

  const warningAssignableRoles = useMemo(
    () => selectedRoles.filter((role) => rolesAssignableToAgency.includes(role)),
    [selectedRoles]
  )

  const selectedAssignableRoles = warningAssignableRoles

  const applyUpdatedStaffMember = (updatedForm: StaffMemberFormValues) => {
    if (isDirtyEqual(staffMemberForm, updatedForm)) {
      return
    }

    setStaffMemberForm(updatedForm)

    const hasFormChanges = !isDirtyEqual(updatedForm, emptyStaffMember)

    setIsFormDirty(hasFormChanges)
    if (hasFormChanges) setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED)

    if (saveChangesActivated) {
      const updatedErrors = [
        ...new Set(validateStaffMemberForm(updatedForm, showStateSelect)),
      ]

      if (!isEqual(errorsList, updatedErrors)) {
        setErrorsList(updatedErrors)
      }
    }
  }

  const { data: agencies, isPending: agenciesPending } = useGetAllAgencies()

  const hasRoleAssignmentConflictCheck =
    roleIsAssignableToAgency && agencyIds.length > 0 && selectedAssignableRoles.length > 0
  const agenciesApi = useContext(AgenciesApiContext)

  const { createStaffMember, createStaffMemberPending } = useCreateStaffMember({
    onSuccess: async (createdStaffMember, inputData) => {
      try {
        setIsFormDirty(false)
        await invalidateGetAllStaff()

        const email = staffMemberForm.email
        const roles = staffMemberForm.roles ?? []

        if (!email || roles.length === 0) {
          setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
          return
        }

        const staffRoles = roles
          .map((role) => getEnumValueByValue(StaffRole, role))
          .filter((role): role is StaffRole => role !== undefined)

        if (staffRoles.length === 0) {
          setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
          return
        }

        await assignStaffMemberToAgencies({
          staffMemberId: email,
          staffRoles,
          agencyIds: roleIsAssignableToAgency ? agencyIds : [],
        })

        if (selectedAssignableRoles.length > 0) {
          await Promise.all(
            selectedAssignableRoles.map((role) =>
              invalidateGetStaffMembersAssignedToAgencies(role, agencyIds)
            )
          )
        }

        const agencyIdsToInvalidate = Array.from(
          new Set(
            [selectedAgency?.id, ...agencyIds].filter(
              (id): id is string => !!id
            )
          )
        )

        if (agencyIdsToInvalidate.length > 0) {
          await Promise.all(
            agencyIdsToInvalidate.map((id) => invalidateUrbanXTeamForAgency(id))
          )
        }

        await queryClient.invalidateQueries({
          queryKey: ['getStaffByRolesLoader'],
        })

        setSaveButtonState(SAVE_BUTTON_STATES.SAVED)
        setToastState({
          color: 'success',
          message: 'Staff member created successfully',
          open: true,
        })

        const createdIdentifier = createdStaffMember?.id || inputData?.id

        if (createdIdentifier) {
          await invalidateGetAllStaff()
          navigate(
            `${Pages.EDIT_STAFF}/${encodeURIComponent(createdIdentifier)}`,
            {
              replace: true,
              state: {
                createdStaff: {
                  ...inputData,
                  id: createdStaffMember?.id ?? inputData?.id,
                  email: createdStaffMember?.email ?? inputData?.email,
                } as StaffMemberFormValues,
              },
            }
          )
        }
      } catch {
        setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
        setToastState({
          color: 'error',
          message: 'Error saving changes',
          open: true,
        })
      }
    },
    onError: () => {
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
      setToastState({
        color: 'error',
        message: 'Error saving changes',
        open: true,
      })
    },
  })

  const { assignStaffMemberToAgencies, assignStaffMemberToAgenciesPending } =
    useAssignStaffMemberToAgencies({
      onSuccess: () => undefined,
      onError: () => {
        setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
        setToastState({
          color: 'error',
          message: 'Error occurred assigning staff to agencies',
          open: true,
        })
      },
    })

  const confirmCreateStaffMember = () => {
    setShowWarningModal(false)
    setPendingRoleAssignmentCheck(false)
    createStaffMember(staffMemberForm)
  }

  const checkCurrentRoleAssignments = async () => {
    if (hasRoleAssignmentConflictCheck) {
      try {
        const conflictResponses = await Promise.all(
          selectedAssignableRoles.map(async (role) => {
            const response = await agenciesApi.getStaffMembersAssignedToAgencies(
              role,
              agencyIds
            )

            return {
              staffRole: response.staffRole ?? role,
              staffRoleAssignments: response.staffRoleAssignments ?? [],
            } as UrbanXTeamByAgenciesResponse
          })
        )

        const dedupedRoleConflicts = dedupeRoleConflicts(conflictResponses)

        if (warningAssignableRoles.length > 0 && dedupedRoleConflicts.length > 0) {
          setRoleConflicts(dedupedRoleConflicts)
          setPendingRoleAssignmentCheck(false)
          setShowWarningModal(true)
          return
        }
      } catch {
        setPendingRoleAssignmentCheck(false)
        setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
        setToastState({
          color: 'error',
          message: 'Error validating role assignments',
          open: true,
        })
        return
      }
    }

    setRoleConflicts([])
    confirmCreateStaffMember()
  }

  const onSaveChanges = () => {
    setSaveChangesActivated(true)
    const errors = [
      ...new Set(validateStaffMemberForm(staffMemberForm, showStateSelect)),
    ]
    setErrorsList(errors)

    if (errors.length > 0) {
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR)
      return
    }

    setSaveButtonState(SAVE_BUTTON_STATES.SAVING)
    setPendingRoleAssignmentCheck(true)
  }

  useEffect(() => {
    if (!pendingRoleAssignmentCheck) {
      return
    }

    void checkCurrentRoleAssignments()
  }, [
    pendingRoleAssignmentCheck,
    hasRoleAssignmentConflictCheck,
    warningAssignableRoles,
    selectedAssignableRoles,
    agencyIds,
    agenciesApi,
  ])

  const { mutate: uploadPublicDocument, isPending: isUploading } = useMutation({
    mutationFn: UploadPublicDocument,
    onSuccess: (value) => {
      setImg(value?.publicPath || '')
      applyUpdatedStaffMember({
        ...staffMemberForm,
        photo: value as UploadedDocument,
      })
    },
    onError: () => {
      setToastState({
        color: 'error',
        message: 'Error uploading photo',
        open: true,
      })
    },
  })

  const onUploadPhoto = (file: File) => {
    setImg(URL.createObjectURL(file))
    return new Promise((resolve, reject) => {
      uploadPublicDocument(
        {
          selectedFile: file,
          AgencyId: 'UrbanX',
          getAuthToken,
        },
        {
          onSuccess: resolve,
          onError: reject,
        }
      )
    })
  }

  const {
    removePublicDocument: removePhoto,
    removePublicDocumentPending: isRemovingPhoto,
  } = useRemovePublicDocument({
    onSuccess: () => {
      setImg(undefined)
      applyUpdatedStaffMember({
        ...staffMemberForm,
        photo: undefined,
      })
    },
    onError: () => {
      setToastState({
        color: 'error',
        message: 'Error removing photo',
        open: true,
      })
    },
  })

  const isSavingChanges =
    createStaffMemberPending || assignStaffMemberToAgenciesPending
  const isPhotoActionPending = isUploading || isRemovingPhoto

  const saveChangesState = useMemo(() => {
    if (isSavingChanges) return SAVE_BUTTON_STATES.SAVING
    if (saveButtonState === SAVE_BUTTON_STATES.ERROR)
      return SAVE_BUTTON_STATES.ERROR
    if (isFormDirty) return SAVE_BUTTON_STATES.UNSAVED
    return saveButtonState
  }, [isFormDirty, isSavingChanges, saveButtonState])

  const agenciesOptions = useMemo(() => {
    return mapAgenciesToOptions(agencies, agenciesPending)
  }, [agencies, agenciesPending])

  const onBackClick = () => {
    if (isFormDirty) {
      setForceCheck(true)
      return
    }

    onBack()
  }

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.TEAM}
      isStaff
    >
      <ContentLayout
        hasSecondaryMenu
        secondaryActiveMenu={MenuTitles.TEAM}
        isStaff
      >
        {agenciesPending ? (
          <AgxEmptyState variant="minimal" label="Loading" />
        ) : (
          <AgxSpacer direction="vertical" gap="spacing-5xl" fill>
            <FormPrompt
              hasUnsavedChanges={isFormDirty}
              forceCheck={forceCheck}
              resetForceCheck={(value: boolean) => setForceCheck(value)}
              onBack={onBack}
            />

            <AgxToast selector="#agxToast" toastState={toastState} />

            <div className="borderBottomContainer">
              <AgxButton
                id="return-button"
                variant="naked"
                text="Create Staff Member"
                leftIcon={
                  <Icon_Arrow direction="left" height={22} width={22} />
                }
                onClick={onBackClick}
              />

              <AgxSpacer
                direction="horizontal"
                className="growOne"
                contentJustification="end"
                contentAlignment="center"
              >
                <AgxSaveChangesButton
                  id="btnSaveChanges"
                  text="Save Changes"
                  variant="primary"
                  size="medium"
                  state={saveChangesState}
                  onClick={() => {
                    void onSaveChanges()
                  }}
                  disabled={
                    isPhotoActionPending ||
                    isSavingChanges ||
                    errorsList.length > 0 ||
                    !isFormDirty
                  }
                />
              </AgxSpacer>
            </div>

            <StaffMemberFormPage
              pageAction={StaffAction.CREATE}
              staffMember={staffMemberForm}
              photoUri={img}
              isSaving={isSavingChanges}
              isPhotoActionPending={isPhotoActionPending}
              errorsList={errorsList}
              emailErrorText={
                !staffMemberForm.email
                  ? 'Email required'
                  : !isEmailValid(staffMemberForm.email)
                    ? 'Please specify a valid email address'
                    : ''
              }
              saveChangesActivated={saveChangesActivated}
              availableStaffRoleOptions={availableStaffRoleOptions}
              stateOptions={staffStateOptions}
              showAgencies={roleIsAssignableToAgency}
              agencyIds={agencyIds}
              agenciesOptions={agenciesOptions}
              showEmailInvitation={false}
              emailInviteState={NotificationState.NotSent}
              emailInviteDisabled
              onUpdateStaffMember={applyUpdatedStaffMember}
              onAgencyIdsChange={(value: string[]) => {
                if (!isEqual(agencyIds, value)) {
                  setAgencyIds(value)
                  setIsFormDirty(true)
                  setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED)
                }
              }}
              onUploadPhoto={onUploadPhoto}
              onRemovePhoto={() => {
                removePhoto({
                  filePath: staffMemberForm.photo?.containerFilePath || '',
                })
              }}
            />

            {showWarningModal && (
              <RoleAssignmentModal
                setShowModal={setShowWarningModal}
                updateRoleAssignments={confirmCreateStaffMember}
                selectedRoles={selectedAssignableRoles}
                roleConflicts={roleConflicts}
              />
            )}
          </AgxSpacer>
        )}
      </ContentLayout>
    </PageLayout>
  )
}

export default CreateStaffMember
